import React, {useContext, useEffect, useState} from "react";
import axios from "@common/axios";
import {Checkbox, Form, Input, Select, Alert} from "antd";
import GenericDialog from "@common/GenericDialog";
import {UserContext} from "@context/UserContext";
import {BrandContext} from "@context/BrandContext";

const AddEditClassDialog = (props) => {
    const {clazz, open, setClose, callback} = props;
    const [options, setOptions] = useState({});
    const [formError, setFormError] = useState(null);
    const {user} = useContext(UserContext);
    const brandConfig = useContext(BrandContext);
    const [addClassForm] = Form.useForm();

    const closeDialog = () => {
        if (typeof setClose === "function") {
            setClose();
        }
        setFormError(null);
    };

    useEffect(() => {
        if (open) {
            axios.get('/web/ui/sc/getOptions')
                .then(response => {
                    setOptions(response.data);
                })
                .catch(error => {
                    console.error("Failed to fetch options", error);
                });
        }
    }, [open]);

    useEffect(() => {
        if (Object.keys(options).length !== 0) {
            addClassForm.setFieldsValue({
                clazz: {
                    className: clazz?.className,
                    calendarYear: clazz?.calendarYear && options?.calenderYears?.find(y => y.value === clazz.calendarYear)
                        ? clazz.calendarYear
                        : new Date().getFullYear(),
                    schoolYear: clazz?.schoolYear && options?.schoolYears?.find(y => y.value === clazz.schoolYear)
                        ? clazz.schoolYear
                        : options?.schoolYears?.[0]?.value,
                    hideWorkspaces: clazz?.hideWorkspaces || false,
                    markingType: brandConfig?.questionClusterMarkingTypes
                        ? brandConfig.questionClusterMarkingTypes[0]
                        : undefined,
                    showSolutions: clazz?.showSolutions ?? brandConfig.showSolutions,
                    showWorkedSolutions: clazz?.showWorkedSolutions ?? false
                }
            });
        }
    }, [options, clazz, brandConfig, addClassForm]);

    const handleSave = async () => {
        setFormError(null);

        try {
            // Validate fields first
            await addClassForm.validateFields();

            // Determine the URL based on whether we are editing or adding
            const url = clazz
                ? `/web/ui/sc/updateClass?classId=${clazz.classId}`
                : '/web/ui/sc/newClass';

            // Get form values
            const addClassFormValues = addClassForm.getFieldsValue();

            // Make the POST request
            const response = await axios.post(url, addClassFormValues.clazz, {
                headers: { 'Content-Type': 'application/json' }
            });

            // If there's a callback, call it with the response data
            if (typeof callback === "function") {
                callback(response.data);
            }

            // Close the dialog after successful save
            closeDialog();
        } catch (error) {
            // If the error has a validation component, it's already handled by Form.Item rules.
            // Otherwise, handle Axios errors:
            if (error.response?.data?.message) {
                setFormError(error.response.data.message);
            } else if (!error.errorFields) {
                // If it's not a validation error and no message is available
                setFormError('An error occurred.');
            }
        }
    };

    const dialogButtons = [
        {
            name: clazz ? 'Save' : 'Add',
            callback: handleSave
        },
        {
            name: 'Cancel',
            callback: closeDialog
        }
    ];

    return (
        <GenericDialog
            title={clazz ? "Edit Class" : "Add Class"}
            open={open}
            onClose={closeDialog}
            buttons={dialogButtons}
            contentStyle={{ width: 500 }}
        >
            {formError && (
                <Alert
                    style={{ marginBottom: 16 }}
                    message="Error"
                    description={formError}
                    type="error"
                    showIcon
                />
            )}
            <Form
                form={addClassForm}
                layout="vertical"
            >
                <Form.Item
                    label="Name"
                    name={['clazz', 'className']}
                    rules={[
                        { required: true, message: 'Enter a classname' }
                    ]}
                    style={{ marginBottom: 8 }}
                >
                    <Input />
                </Form.Item>

                {options?.calenderYears && (
                    <Form.Item
                        label="Calendar Year"
                        name={['clazz', 'calendarYear']}
                        style={{ marginBottom: 8 }}
                    >
                        <Select
                            options={options.calenderYears.map(y => ({value: y.value, label: y.label}))}
                        />
                    </Form.Item>
                )}

                {options?.schoolYears && (
                    <Form.Item
                        label="School Year"
                        name={['clazz', 'schoolYear']}
                        style={{ marginBottom: 8 }}
                    >
                        <Select
                            options={options.schoolYears.map(y => ({value: y.value, label: y.label}))}
                        />
                    </Form.Item>
                )}

                {brandConfig?.questionClusterMarkingTypes?.length > 1 && (
                    <Form.Item
                        label="Marking"
                        name={['clazz', 'markingType']}
                        style={{ marginBottom: 8 }}
                    >
                        <Select
                            options={brandConfig.questionClusterMarkingTypes.map(type => ({ value: type, label: type }))}
                        />
                    </Form.Item>
                )}

                {brandConfig?.allowHideWorkspaces && (
                    <Form.Item
                        name={['clazz', 'hideWorkspaces']}
                        valuePropName="checked"
                    >
                        <Checkbox>Hide Workspaces</Checkbox>
                    </Form.Item>
                )}

                {!brandConfig?.hideClassShowSolutions && (
                    <Form.Item
                        name={['clazz', 'showSolutions']}
                        valuePropName="checked"
                    >
                        <Checkbox>Show Answers / Suggested Responses</Checkbox>
                    </Form.Item>
                )}

                {!brandConfig?.hideClassShowWorkedSolutions && (
                    <Form.Item
                        name={['clazz', 'showWorkedSolutions']}
                        valuePropName="checked"
                    >
                        <Checkbox>Show Worked Solutions</Checkbox>
                    </Form.Item>
                )}
            </Form>
        </GenericDialog>
    );
};

export default AddEditClassDialog;
